import { addWeeks, formatISO } from "date-fns";
import { getTimezoneOffset, utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";

export function getFutureDates(inputDate: Date, dayOfWeek: number, weeks: number, timeZone: string) {
  const zonedStart = utcToZonedTime(inputDate, timeZone);

  // Get the next occurrence of the specified day of the week
  // Get seven days from the input date, then find the next occurrence of the specified day
  const firstOccurrence = getNextOccurance(zonedStart, dayOfWeek) as Date;

  const futureDates: string[] = [];

  for (let i = 0; i < weeks; i++) {
    const dateInZone = addWeeks(firstOccurrence, i);
    const utcDate = zonedTimeToUtc(dateInZone, timeZone); // convert back to UTC if needed
    futureDates.push(formatISO(utcDate, { representation: "date" }));
  }

  return futureDates;
}

export function formatDate(date: Date) {
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  const day = String(date.getDate()).padStart(2, "0");
  const month = months[date.getMonth()];
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
}

/**
 * getFutureTimezone calculates the timezone for any given date
 * @param date - The date to calculate the timezone for
 * @returns The timezone for the given date
 */
export function getFutureTimezone(date: string, currentUserTimezone: string) {
  // Format the last date to get its timezone abbreviation
  const formatter = new Intl.DateTimeFormat("en-GB", {
    timeZone: currentUserTimezone,
    timeZoneName: "short",
  });

  const parts = formatter.formatToParts(new Date(date));

  const timeZonePart = parts.find(part => part.type === "timeZoneName");

  if (timeZonePart) {
    // Convert lastDate.event_date to a Date object in the correct timezone
    const eventDate = new Date(date);

    // Get the timezone offset in minutes and convert to hours
    const timezoneOffsetMinutes = new Date(eventDate).getTimezoneOffset();
    const timezoneOffsetHours = -timezoneOffsetMinutes / 60;

    return {
      timezone: currentUserTimezone,
      short_timezone: timeZonePart.value,
      timezone_offset: timezoneOffsetHours,
    };
  }

  const userDate = new Date(date);
  const userTimezone = new Intl.DateTimeFormat("en-GB", { timeZone: currentUserTimezone }).resolvedOptions().timeZone;

  // If the timezone abbreviation is not found, return the user's timezone
  return {
    timezone: currentUserTimezone,
    short_timezone: userTimezone,
    timezone_offset: new Date(userDate).getTimezoneOffset() / 60,
  };
}

export function getTimezoneInfo(date: string | Date, timeZone: string) {
  const zonedDate = utcToZonedTime(date, timeZone);

  const formatter = new Intl.DateTimeFormat("en-GB", {
    timeZone,
    timeZoneName: "short",
  });

  const shortTz = formatter.formatToParts(zonedDate).find(p => p.type === "timeZoneName")?.value ?? "";

  const offset = -getTimezoneOffset(timeZone, zonedDate) / 60;

  return {
    short_timezone: shortTz,
    timezone_offset: offset,
  };
}


const getNextOccurance = (date: Date, dayOfWeek: number) => {

  const nextSevenDays = [];

  for (let i = 0; i < 8; i++) {
    const nextDate = new Date(date);
    nextDate.setDate(date.getDate() + i);
    nextSevenDays.push(nextDate);
  }

  // Find the next occurrence of the specified day of the week that isn't today or earlier
  const nextOccurrence = nextSevenDays.find(d => d.getDay() === dayOfWeek && d > date);
  return nextOccurrence;
};